import React, { useState } from 'react';

import config from 'config';
import styled from 'styled-components';

import DropdownMenu from '~/shared/components/DropdownMenu';
import { AssetContainer as BaseAssetContainer } from '~/techstyle-shared/react-assets';
import {
  Button,
  desktop,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

const DynamicFormattedMessage = FormattedMessage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.fitGuideBackground};
  padding-top: 18px;
  ${mobile`
    padding-top: 27px;
  `}
`;

const Header = styled.h1`
  ${({ theme }) => theme.typography.values.Header32};
  text-align: center;
`;

const MobileDropdownWrapper = styled.div`
  padding: 24px;
  width: 100%;
  > div {
    width: 100%;
  }
  ${desktop`
   display: none;
  `}
`;

const DesktopTabsWrapper = styled.div`
  padding: 24px;
  width: 100%;
  display: flex;
  justify-content: center;
  ${mobile`
    display: none;
  `}
`;

const TabButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  ${({ theme }) => theme.fitGuideTabButton};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  background: ${({ theme }) => theme.colors.backgroundFitGuideContent};
  > div {
    max-width: 900px;
  }
`;

const ContentHeaderWrapper = styled.div`
  ${({ theme }) => theme.fitGuideHeader};
`;

const ContentSubHeaderWrapper = styled.div`
  ${({ theme }) => theme.typography.values.Header22};
  margin-bottom: 20px;
  ${mobile`
    ${({ theme }) => theme.typography.values.Header14med}; 
    margin-bottom: 16px;
  `}
`;

const ContentParagraphWrapper = styled.div`
  ${({ theme }) => theme.typography.values.Body18};
  ${mobile`
    ${({ theme }) => theme.typography.values.Body14}; 
  `}
`;

const ContentAssetWrapper = styled.div`
  width: 100%;
  margin-top: 22px;
  ${mobile`
    margin-top: 20px;
  `}
  img {
    margin: 20px 0;
  }
`;

const DropdownMenuListItem = styled(DropdownMenu.ListItem)`
  ${({ theme }) => theme.fitGuideDropdownMenuListItem};
`;

const AssetContainer = styled(BaseAssetContainer)`
  width: 100%;
`;

function FitGuide() {
  const CONTENT_CHOICES = config.get('public.brand.fitGuideContentChoices');
  const firstOption = Object.keys(CONTENT_CHOICES)[0];
  const [contentPage, updateContentPage] = useState(
    CONTENT_CHOICES[firstOption]
  );
  const [contentIndex, updateContentIndex] = useState(0);

  const [activeTab, setActiveTab] = useState(CONTENT_CHOICES[firstOption]);
  const { isMobile } = useBreakpoint();

  const choices = Object.entries(CONTENT_CHOICES).map(
    ([choiceEnum, choiceString]) => {
      return {
        name: choiceString,
        value: choiceEnum,
      };
    }
  );

  const getHandle = (contentName) => {
    return contentName.split(' ')[0].toLowerCase();
  };

  const { formatMessage } = useIntl();

  return (
    <Container>
      <Header>
        <FormattedMessage
          defaultMessage="Fit Guide"
          id="fit_guide.page_header"
        />
      </Header>
      <MobileDropdownWrapper>
        <DropdownMenu options={choices} value={contentPage}>
          <DropdownMenu.Button data-autotag="fitguide-dropdown-button">
            {formatMessage({
              id: `fit_guide.dropdown_option_${contentIndex + 1}`,
              defaultMessage: contentPage,
            })}
          </DropdownMenu.Button>
          <DropdownMenu.List>
            {choices.map(({ name, value }, i) => {
              return (
                <DropdownMenuListItem
                  value={name}
                  key={name}
                  onClick={() => {
                    updateContentPage(CONTENT_CHOICES[value]);
                    updateContentIndex(i);
                  }}
                  className={
                    CONTENT_CHOICES[value] === contentPage ? 'active' : ''
                  }
                >
                  <DynamicFormattedMessage
                    defaultMessage={name}
                    id={`fit_guide.dropdown_option_${i + 1}`}
                  />
                </DropdownMenuListItem>
              );
            })}
          </DropdownMenu.List>
        </DropdownMenu>
      </MobileDropdownWrapper>
      <DesktopTabsWrapper>
        {choices.map(({ name, value }, i) => {
          return (
            <TabButton
              key={name}
              onClick={() => {
                updateContentPage(CONTENT_CHOICES[value]);
              }}
              onMouseEnter={() => setActiveTab(CONTENT_CHOICES[value])}
              onMouseLeave={() => setActiveTab(contentPage)}
              className={CONTENT_CHOICES[value] === activeTab ? 'active' : ''}
            >
              <DynamicFormattedMessage
                defaultMessage={name}
                id={`fit_guide.dropdown_option_${i + 1}`}
              />
            </TabButton>
          );
        })}
      </DesktopTabsWrapper>

      <ContentWrapper>
        <ContentHeaderWrapper>
          <DynamicFormattedMessage
            id={`fit_guide.${getHandle(contentPage)}_header`}
            defaultMessage={contentPage}
          />
        </ContentHeaderWrapper>
        <ContentSubHeaderWrapper>
          <DynamicFormattedMessage
            id={`fit_guide.${getHandle(contentPage)}_subheader`}
            defaultMessage={contentPage}
          />
        </ContentSubHeaderWrapper>
        <ContentParagraphWrapper>
          <DynamicFormattedMessage
            id={`fit_guide.${getHandle(contentPage)}_paragraph`}
            defaultMessage={contentPage}
          />
        </ContentParagraphWrapper>
        <ContentAssetWrapper>
          <AssetContainer
            name={
              isMobile
                ? `fit_guide_${getHandle(contentPage)}_1m`
                : `fit_guide_${getHandle(contentPage)}_1d`
            }
          />

          <AssetContainer
            name={
              isMobile
                ? `fit_guide_${getHandle(contentPage)}_2m`
                : `fit_guide_${getHandle(contentPage)}_2d`
            }
          />
          {contentPage === CONTENT_CHOICES.COMPRESSION_GUIDE && (
            <AssetContainer
              name={
                isMobile
                  ? `fit_guide_${getHandle(contentPage)}_3m`
                  : `fit_guide_${getHandle(contentPage)}_3d`
              }
            />
          )}
        </ContentAssetWrapper>
      </ContentWrapper>
    </Container>
  );
}

export default FitGuide;
